import styled, { createGlobalStyle, css, keyframes } from 'styled-components';

import { BORDER_GLOW } from '../../theme/tokens';

export type BorderGlowMode = 'pointer' | 'spin';

/** Shared layer props: `$lit` = full-opacity (hover/intro-lit). On hover the cone gains a
 *  dim floor (`--bg-cone-floor`) so the whole ring stays faintly lit while the bright arc
 *  still follows the cursor angle. */
type LayerProps = { $mode: BorderGlowMode; $lit?: boolean };

/**
 * Registers the arc-centre angle as an animatable `<angle>` custom property (once;
 * `createGlobalStyle` dedupes across instances) so `mode='spin'` can interpolate it
 * via a keyframe. `inherits: true` lets the spin/sweep code set it on the root and
 * have every layer pick it up. Engines without `@property` fall back to a static arc.
 */
export const AngleProperty = createGlobalStyle`
  @property --bg-cursor-angle {
    syntax: '<angle>';
    inherits: true;
    initial-value: 90deg;
  }
`;

/** One full revolution of the lit arc around the ring (mode 'spin'). */
const spin = keyframes`
  from { --bg-cursor-angle: 0deg; }
  to { --bg-cursor-angle: 360deg; }
`;

/** Edge-proximity opacity expressions (math only; embedded in calc()). */
const proxGlow =
  '(var(--bg-edge-proximity, 0) - var(--bg-sensitivity)) / (100 - var(--bg-sensitivity)) * var(--bg-intensity)';
const proxColor =
  '(var(--bg-edge-proximity, 0) - var(--bg-color-sensitivity)) / (100 - var(--bg-color-sensitivity))';

/** Opacity for a layer: full when spin OR hover-lit; otherwise the proximity ramp (intro/rest). */
const layerOpacity = (full: string, prox: string) => (p: LayerProps) =>
  p.$mode === 'spin' || p.$lit ? css`opacity: ${full};` : css`opacity: calc(${prox});`;

/** Apply a cursor cone mask (always on; its dim floor gives the full-ring hover base). */
const cone = (mask: string): ReturnType<typeof css> => css`
  -webkit-mask-image: ${mask};
  mask-image: ${mask};
`;

/**
 * The crisp coloured mesh border — the cursor-cone gate (or full ring on hover). Its child
 * {@link BorderMeshCore} carries the mesh + the ring cut; nesting keeps the interior
 * TRANSPARENT so this layer never occludes the inner glow beneath it.
 */
export const BorderMesh = styled.span<LayerProps>`
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  transition: opacity ${BORDER_GLOW.FADE_MS}ms ease-out;
  ${cone(BORDER_GLOW.BORDER_CONE)};
  ${layerOpacity('1', proxColor)};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/** The mesh paint for {@link BorderMesh}, cut to just the border ring (transparent inside). */
export const BorderMeshCore = styled.span`
  position: absolute;
  inset: 0;
  border: calc(var(--bg-ring-w) * 1px) solid transparent;
  border-radius: inherit;
  background: ${BORDER_GLOW.MESH_BG};
  -webkit-mask: ${BORDER_GLOW.RING_MASK};
  mask: ${BORDER_GLOW.RING_MASK};
  -webkit-mask-composite: xor;
  mask-composite: exclude;
`;

/**
 * INNER glow — light bleeding INWARD from the rim onto the card face. NOT cone-masked (a
 * conic mask attenuates a `box-shadow` ~6x, measured), so it is a full-ring inset shadow
 * gated by OPACITY. Shown on spin and on hover; OFF during the intro sweep (a full-ring inner
 * glow there would read as an always-on border, drowning out the travelling arc — the intro
 * must show ONLY the moving glow, which the cone-masked border + halo arc already provide).
 */
export const InnerGlow = styled.span<LayerProps>`
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  box-shadow: ${BORDER_GLOW.INNER_SHADOW};
  transition: opacity ${BORDER_GLOW.FADE_MS}ms ease-out;

  /* Full on spin; a DIM uniform base on hover (it can't follow the cursor since a cone mask
     would attenuate the inset shadow), so the mouse-following read comes from the border +
     halo arc. OFF on the intro sweep (else) so only the travelling arc shows. */
  ${(p) =>
    p.$mode === 'spin'
      ? css`opacity: var(--bg-intensity);`
      : p.$lit
        ? css`opacity: calc(var(--bg-intensity) * 0.4);`
        : css`opacity: 0;`}

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/**
 * OUTER halo — a layer extending `glowRadius` beyond the card; its `::before` (back at the
 * card edge) carries the outset shadow stack. Narrow cone (concentrated hot-spot) normally;
 * full ring on hover. Additive.
 */
export const OuterGlow = styled.span<LayerProps>`
  position: absolute;
  inset: calc(var(--bg-glow-radius) * -1px);
  border-radius: inherit;
  pointer-events: none;
  mix-blend-mode: plus-lighter;
  transition: opacity ${BORDER_GLOW.FADE_MS}ms ease-out;
  ${cone(BORDER_GLOW.EDGE_CONE)};
  ${layerOpacity('var(--bg-intensity)', proxGlow)};

  &::before {
    content: '';
    position: absolute;
    inset: calc(var(--bg-glow-radius) * 1px);
    border-radius: inherit;
    box-shadow: ${BORDER_GLOW.OUTER_SHADOW};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/**
 * Colour inner-glow — the cone gate (or full ring on hover). Its child {@link FillCore}
 * carries the mesh + the edge vignette; nesting intersects the two masks reliably.
 */
export const Fill = styled.span<LayerProps>`
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  transition: opacity ${BORDER_GLOW.FADE_MS}ms ease-out;
  ${cone(BORDER_GLOW.BORDER_CONE)};
  ${layerOpacity('var(--bg-fill-opacity)', `var(--bg-fill-opacity) * ${proxColor}`)};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/** The mesh paint for {@link Fill}, masked to the edge vignette (fades inward). Additive. */
export const FillCore = styled.span`
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: ${BORDER_GLOW.FILL_BG};
  -webkit-mask-image: ${BORDER_GLOW.FILL_VIGNETTE};
  mask-image: ${BORDER_GLOW.FILL_VIGNETTE};
  mix-blend-mode: plus-lighter;
`;

/**
 * Overlay root: fills its positioned host (a card), sits behind the host's content
 * (`z-index: -1`, like the existing pack glow) and never eats pointer events. In
 * `mode='spin'` it drives the shared arc angle; the intro sweep sets it from JS. The host
 * must establish a stacking context (a transform / filter / `isolation`) for the
 * `z-index: -1` to land behind content, and allow `overflow: visible` for the bloom.
 */
export const Root = styled.span<{ $mode: BorderGlowMode }>`
  position: absolute;
  inset: 0;
  z-index: -1;
  border-radius: inherit;
  pointer-events: none;

  ${(p) =>
    p.$mode === 'spin' &&
    css`
      animation: ${spin} ${BORDER_GLOW.SPIN_MS}ms linear infinite;
    `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;
